import { EXTRACT_PRODUCT_FEATURES_PROMPT } from '@/constants/prompts';
import {
  ScriptTemplates,
  DEFAULT_SCRIPT_GUIDANCE,
  SCRIPT_TEMPLATE_SETTINGS,
  getFollowupScriptGuidance,
  SCRIPT_TEMPLATE_PROMPTS,
  FOUNDER_VIDEO_SCRIPT_GUIDANCE,
} from '@/constants/videoUseCases';

export const getExtractFeaturesPrompt = (productDescription: string) => {
  return `
    ${EXTRACT_PRODUCT_FEATURES_PROMPT} 
    - Product description: ${productDescription}
  `;
};

interface ProductReviewPromptProps {
  template: ScriptTemplates;
  reference?: string;
  productDescription?: string;
  featuresToHighlight?: string[];
  targetAudience?: string[];
  userPrompt?: string;
}

export const getProductReviewPrompt = ({
  template,
  reference,
  productDescription,
  featuresToHighlight,
  targetAudience,
  userPrompt,
}: ProductReviewPromptProps) => `
    - ${SCRIPT_TEMPLATE_SETTINGS[template].prompt}
    ${(reference && `- ${getFollowupScriptGuidance(reference)}`) || ''}
    - ${DEFAULT_SCRIPT_GUIDANCE}:
    ${(productDescription && `- Product description: ${productDescription}`) || ''}
    ${(featuresToHighlight?.length && `- Features to highlight: ${featuresToHighlight.join(', ')}`) || ''}
    ${(targetAudience?.length && `- Target audience: ${targetAudience.join(', ')}`) || ''}
    ${userPrompt || ''}
  `;

interface FounderVideoScriptPromptProps {
  reference?: string;
  brandDescription?: string;
  featuresToHighlight?: string[];
}

export const getFounderVideoScriptPrompt = ({
  reference,
  brandDescription,
  featuresToHighlight,
}: FounderVideoScriptPromptProps) => `
      - ${SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.behindTheDesign]}
      ${(reference && `- ${getFollowupScriptGuidance(reference)}`) || ''}
      - ${FOUNDER_VIDEO_SCRIPT_GUIDANCE}:
      ${(brandDescription && `- Brand description: ${brandDescription}`) || ''}
      ${(featuresToHighlight?.length && `- Features to highlight: ${featuresToHighlight.join(', ')}`) || ''}
    `;

type BackgroundAudioPromptProps = {
  productTitle?: string;
  productDescription?: string;
  featuresToHighlight?: string[];
  targetAudience?: string[];
};
export const getBackgroundAudioPrompt = ({
  productTitle,
  productDescription,
  featuresToHighlight,
  targetAudience,
}: BackgroundAudioPromptProps) => `
  You are an experienced prompt writer for AI music generation.
  Create a creative, engaging music prompt based on the following:
  ${productTitle && `- Product: ${productTitle}`}
  ${productDescription && `- Description: ${productDescription}`}
  ${featuresToHighlight?.length && `- Features: ${featuresToHighlight.join(', ')}`}
  ${targetAudience?.length && `- Audience: ${targetAudience.join(', ')}`}
  
  The prompt should inspire the creation of a unique music track suited for the product, focusing on evoking the right mood and emotions.
  The final prompt should be no more than 200 characters. Do not include any explanation or thoughts process, just the result itself.
`;

type ReplaceBackgroundPromptProps = {
  productTitle?: string;
  productDescription?: string;
};

export const getReplaceBackgroundPrompt = ({
  productTitle,
  productDescription,
}: ReplaceBackgroundPromptProps) => `
  Using the following product title and description, generate a concise and simple prompt and negative prompt for an image generation AI (Stability AI Inpaint API).
  Do not mention the product name or include the full description in the prompts. Instead, abstractly describe a realistic and detailed background that complements the product based on its features and intended use.
  Ensure the prompts are short and focus on the background setting, helping to prevent solid color backgrounds or plain backdrops.

  **Output format:**

  [prompt]: (your prompt here)

  [negative_prompt]: (your negative prompt here)

  **Product Title:**
  ${productTitle || 'not specified'}

  **Product Description:**
  ${productDescription || 'not specified'}
`;

export const parseReplaceBackgroundResult = (
  text: string
): { prompt: string; negativePrompt: string } => {
  const promptMatch = text.match(/\[prompt\]:\s*(.+)/i);
  const negativePromptMatch = text.match(/\[negative_prompt\]:\s*(.+)/i);

  const prompt = promptMatch ? promptMatch[1].trim() : '';
  const negativePrompt = negativePromptMatch
    ? negativePromptMatch[1].trim()
    : '';

  return { prompt, negativePrompt };
};

export type VideoFromImagePromptProps = {
  productTitle?: string;
  productDescription?: string;
  backgroundDescription?: string;
};

export const getVideoFromImagePrompt = ({
  productTitle,
  productDescription,
  backgroundDescription,
}: VideoFromImagePromptProps) => `
    Using the product information below, generate a concise prompt (maximum 512 characters) for an AI video generation service to create an amazing video.
    Focus on describing the visual elements, scenes, and actions happening in the video without mentioning the product name or details.
    If a background description is provided, incorporate it to enhance the video's appeal.
    Respond with the prompt only, without explanation or thoughts process.

    Product Title: ${productTitle || 'not specified'}

    Product Description: ${productDescription || 'not specified'}

    Background Description (optional): ${backgroundDescription || 'not specified'}
`;

export const getVideoFromTextPrompt = (
  productDescription: string,
  script: string
) => `
  You are an expert in creating video prompts for AI video generation services.
  Using the following product description and video script, generate a concise one sentence prompt (maximum 300 characters) for an AI video generation service to create an amazing video shot.
  Focus on describing the visual elements, and action happening in the shot.
  Respond with the prompt only, without explanation or thoughts process.
  The goal is to create a beauty shot that would visually complement part of the video script.
  Keep the prompt short, simple and concise, describing a single scene or action.
  The product itself should not be visible in the final video shot.

  Product Description: ${productDescription}
  Video Script: ${script}
`;

export const getSummarizeVideoFromTextPrompt = (prompt: string) => `
  You are an expert in summarizing video scripts.
  Based on the following video script, extract 3 keywords that summarize the prompt.
  reply with the keywords only separated by commas, without explanation or thoughts process.
  ${prompt}
`;

export const getSuggestedAssetsPrompt = (
  videoScript: string,
  productDescription: string
) => `
  You are an expert in recommending assets for video creation.
  Based on the following video script and productDescription, recommend 3 different search queries for stock videos that would be relevant for the video.
  Suggest terms that are relevant to the product described below. It should extract related topics or themes based on the product or that are mentioned in the video script.

  Video script:${videoScript}

  Product description: ${productDescription}
  
  Additional guidance:
  - the response should be an array of strings.
  - each search query should be up to 3 words long  
  - extract 3 search queries
  - don't include newline or bullet points in the response
  - output format: ["searchQuery1", "searchQuery2", "searchQuery3"]
`;
